import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../utils/api';

const AcceptInvitation = ({ user }) => {
  const [searchParams] = useSearchParams();
  const invitationId = searchParams.get('invitation');
  const [status, setStatus] = useState('idle'); // idle | loading | success | error | login_required | set_password | choose_auth
  const [message, setMessage] = useState('');
  const [formData, setFormData] = useState({ password: '', confirmPassword: '' });
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!invitationId) {
      setStatus('error');
      setMessage('Missing invitation.');
      return;
    }

    const accessToken = localStorage.getItem('access_token');

    if (!accessToken) {
      setStatus('choose_auth');
      setMessage('');
      return;
    }

    const accept = async () => {
      try {
        setStatus('loading');
        const response = await api.post(`/team/invitation/${invitationId}/accept`, {});
        setStatus('success');
        setMessage(response?.data?.message || 'Invitation accepted.');
        setTimeout(() => navigate('/'), 1200);
      } catch (err) {
        const detail = err?.response?.data?.detail || 'Failed to accept invitation.';
        if (detail.toLowerCase().includes('please sign in')) {
          localStorage.setItem('pending_invitation', invitationId);
          setStatus('login_required');
          setMessage(detail);
          return;
        }
        setStatus('error');
        setMessage(detail);
      }
    };

    accept();
  }, [invitationId, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!invitationId) return;
    if (!formData.password || formData.password.length < 8) {
      setMessage('Password must be at least 8 characters long.');
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      setMessage('Passwords do not match.');
      return;
    }
    try {
      setSubmitting(true);
      setMessage('');
      const response = await api.post(`/team/invitation/${invitationId}/accept-with-password`, {
        password: formData.password
      });
      if (response.data?.access_token) {
        localStorage.setItem('access_token', response.data.access_token);
      }
      if (response.data?.refresh_token) {
        localStorage.setItem('refresh_token', response.data.refresh_token);
      }
      setStatus('success');
      setMessage(response?.data?.message || 'Invitation accepted.');
      setTimeout(() => navigate('/'), 1200);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Failed to accept invitation.';
      if (detail.toLowerCase().includes('please sign in')) {
        localStorage.setItem('pending_invitation', invitationId);
        setStatus('login_required');
        setMessage(detail);
        return;
      }
      setStatus('error');
      setMessage(detail);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="max-w-md w-full bg-white shadow rounded-lg p-6 text-center">
        <h1 className="text-lg font-semibold text-gray-900 mb-2">Team Invitation</h1>
        {status === 'loading' && <p className="text-gray-600">Accepting invitation…</p>}
        {status === 'success' && <p className="text-green-600">{message}</p>}
        {status === 'error' && <p className="text-red-600">{message}</p>}
        {status === 'login_required' && (
          <div className="space-y-3">
            <p className="text-gray-600">{message || 'Please sign in to accept this invitation.'}</p>
            <button
              type="button"
              onClick={() => {
                localStorage.setItem('pending_invitation', invitationId);
                navigate('/');
              }}
              className="w-full px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition"
            >
              Go to Login
            </button>
          </div>
        )}
        {status === 'choose_auth' && (
          <div className="space-y-3 mt-4 text-left">
            <p className="text-sm text-gray-600">
              If you already have an Athena password, sign in first. If this is your first time and no password was set yet, create one now.
            </p>
            <button
              type="button"
              onClick={() => {
                localStorage.setItem('pending_invitation', invitationId);
                navigate('/');
              }}
              className="w-full px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition"
            >
              Sign In First
            </button>
            <button
              type="button"
              onClick={() => {
                setStatus('set_password');
                setMessage('');
              }}
              className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition"
            >
              Set Password Instead
            </button>
          </div>
        )}
        {status === 'set_password' && (
          <form onSubmit={handleSubmit} className="text-left mt-4 space-y-3">
            {message && <p className="text-sm text-red-600">{message}</p>}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Create Password</label>
              <input
                type="password"
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                value={formData.password}
                onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                placeholder="At least 8 characters"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
              <input
                type="password"
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                value={formData.confirmPassword}
                onChange={(e) => setFormData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                placeholder="Re-enter password"
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="w-full px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition"
            >
              {submitting ? 'Setting password...' : 'Accept Invitation'}
            </button>
            <button
              type="button"
              onClick={() => navigate('/')}
              className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition"
            >
              Go to Login
            </button>
          </form>
        )}

        <div className="mt-6 flex items-center justify-center gap-3">
          {status === 'login_required' && (
            <button
              onClick={() => navigate('/')}
              className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition"
            >
              Go to Login
            </button>
          )}
          {(status === 'success' || status === 'error') && (
            <button
              onClick={() => navigate('/settings')}
              className="px-4 py-2 bg-gray-900 text-white rounded-md hover:bg-gray-800 transition"
            >
              Go to Settings
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AcceptInvitation;
